import { useEffect, useId, useRef, useState } from 'react';
import { Icone } from './Icone.tsx';

export type Opcao<T extends string = string> = {
  valor: T;
  rotulo: string;
  descricao?: string;
  desabilitada?: boolean;
};

type Props<T extends string> = {
  opcoes: Opcao<T>[];
  valor: T | null;
  aoMudar: (valor: T) => void;
  rotulo?: string;
  dica?: string;
  erro?: string | null;
  obrigatorio?: boolean;
  /** Mesmo par obrigatorio/opcional do `Campo` — nenhum campo fica ambíguo. */
  opcional?: boolean;
  placeholder?: string;
  desabilitado?: boolean;
  nome?: string;
};

/**
 * Select próprio, no lugar do `<select>` nativo.
 *
 * O nativo não aceita estilo na lista de opções — cada sistema desenha a sua,
 * então o menu nunca combina com o resto da interface. Trocá-lo custa
 * reimplementar o que o navegador dava de graça, que é o que está abaixo:
 * padrão combobox/listbox da ARIA, navegação por setas, Home/End, busca por
 * digitação, Escape e clique fora.
 */
export function Select<T extends string>({
  opcoes,
  valor,
  aoMudar,
  rotulo,
  dica,
  erro,
  obrigatorio,
  opcional,
  placeholder = 'Selecione',
  desabilitado,
  nome,
}: Props<T>) {
  const id = useId();
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const [acima, setAcima] = useState(false);

  const raiz = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  /** Buffer da busca por digitação, limpo após 600ms de pausa. */
  const digitado = useRef({ texto: '', em: 0 });

  const selecionada = opcoes.find((o) => o.valor === valor) ?? null;

  function abrir() {
    if (desabilitado) return;
    const indice = opcoes.findIndex((o) => o.valor === valor);
    setAtivo(indice >= 0 ? indice : 0);

    // Abre para cima quando não há espaço embaixo — evita a lista sair da tela
    // num campo perto do rodapé.
    const caixa = gatilho.current?.getBoundingClientRect();
    setAcima(!!caixa && window.innerHeight - caixa.bottom < 260 && caixa.top > 260);

    setAberto(true);
  }

  function fechar(devolverFoco = true) {
    setAberto(false);
    if (devolverFoco) gatilho.current?.focus();
  }

  function escolher(indice: number) {
    const opcao = opcoes[indice];
    if (!opcao || opcao.desabilitada) return;
    aoMudar(opcao.valor);
    fechar();
  }

  /** Pula opções desabilitadas ao navegar. */
  function mover(de: number, passo: number) {
    let i = de;
    for (let tentativas = 0; tentativas < opcoes.length; tentativas++) {
      i = (i + passo + opcoes.length) % opcoes.length;
      if (!opcoes[i]?.desabilitada) return i;
    }
    return de;
  }

  useEffect(() => {
    if (!aberto) return;

    function foraDoComponente(e: MouseEvent) {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', foraDoComponente);
    return () => document.removeEventListener('mousedown', foraDoComponente);
  }, [aberto]);

  // Mantém a opção ativa visível ao navegar por teclado numa lista rolável.
  useEffect(() => {
    if (!aberto) return;
    lista.current?.querySelector<HTMLElement>('[data-ativa="true"]')?.scrollIntoView({
      block: 'nearest',
    });
  }, [aberto, ativo]);

  function aoTeclar(e: React.KeyboardEvent) {
    if (!aberto) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        abrir();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        fechar();
        break;
      case 'Tab':
        // Tab confirma e sai, como no select nativo.
        setAberto(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setAtivo((i) => mover(i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setAtivo((i) => mover(i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setAtivo(mover(opcoes.length - 1, 1));
        break;
      case 'End':
        e.preventDefault();
        setAtivo(mover(0, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        escolher(ativo);
        break;
      default: {
        if (e.key.length !== 1) return;
        const agora = Date.now();
        const texto =
          (agora - digitado.current.em < 600 ? digitado.current.texto : '') + e.key.toLowerCase();
        digitado.current = { texto, em: agora };

        const achado = opcoes.findIndex(
          (o) => !o.desabilitada && o.rotulo.toLowerCase().startsWith(texto),
        );
        if (achado >= 0) setAtivo(achado);
      }
    }
  }

  return (
    <div className="campo">
      {rotulo && (
        <label className="campo__rotulo" htmlFor={id} id={`${id}-rotulo`}>
          {rotulo}
          {obrigatorio && (
            <span className="campo__obrigatorio" aria-hidden="true">
              *
            </span>
          )}
          {opcional && <span className="campo__opcional">opcional</span>}
        </label>
      )}

      <div className="select" ref={raiz}>
        {/* Espelha o valor num input escondido para funcionar dentro de um
            <form> comum, como o select nativo faria. */}
        {nome && <input type="hidden" name={nome} value={valor ?? ''} />}

        <button
          ref={gatilho}
          id={id}
          type="button"
          role="combobox"
          className={`select__gatilho${erro ? ' campo__casca--erro' : ''}`}
          aria-expanded={aberto}
          aria-haspopup="listbox"
          aria-controls={aberto ? `${id}-lista` : undefined}
          aria-labelledby={rotulo ? `${id}-rotulo ${id}` : undefined}
          aria-invalid={!!erro}
          aria-describedby={dica ? `${id}-dica` : undefined}
          disabled={desabilitado}
          onClick={() => (aberto ? fechar() : abrir())}
          onKeyDown={aoTeclar}
        >
          <span className={`select__valor${selecionada ? '' : ' select__valor--vazio'}`}>
            {selecionada?.rotulo ?? placeholder}
          </span>
          <Icone.Seta tamanho={16} className="select__seta" />
        </button>

        {aberto && (
          <ul
            ref={lista}
            id={`${id}-lista`}
            role="listbox"
            className={`select__lista${acima ? ' select__lista--acima' : ''}`}
            aria-labelledby={rotulo ? `${id}-rotulo` : undefined}
            // O foco fica no gatilho; a lista é apontada por aria-activedescendant.
            aria-activedescendant={`${id}-opcao-${ativo}`}
            tabIndex={-1}
          >
            {opcoes.map((o, i) => (
              <li
                key={o.valor}
                id={`${id}-opcao-${i}`}
                role="option"
                aria-selected={o.valor === valor}
                aria-disabled={o.desabilitada}
                data-ativa={i === ativo}
                className={`select__opcao${i === ativo ? ' select__opcao--ativa' : ''}`}
                style={o.desabilitada ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                onMouseEnter={() => !o.desabilitada && setAtivo(i)}
                // mousedown em vez de click: o click chegaria depois do
                // mousedown que fecha a lista pelo "clique fora".
                onMouseDown={(e) => {
                  e.preventDefault();
                  escolher(i);
                }}
              >
                <span>
                  {o.rotulo}
                  {o.descricao && (
                    <span className="campo__dica" style={{ display: 'block' }}>
                      {o.descricao}
                    </span>
                  )}
                </span>
                {o.valor === valor && <Icone.Check tamanho={15} className="select__marca" />}
              </li>
            ))}
          </ul>
        )}
      </div>

      {erro ? (
        <span className="campo__erro" role="alert">
          <Icone.Alerta tamanho={13} />
          {erro}
        </span>
      ) : (
        dica && (
          <span className="campo__dica" id={`${id}-dica`}>
            {dica}
          </span>
        )
      )}
    </div>
  );
}
